import React, { useContext, useState } from "react";
import styled from "styled-components";

import { Tabs, TextInput, VFlex } from "@examspark/common-ui";
import {
  ResourceVisibilityType,
  SearchableResourceFilter,
} from "@examspark/types";
import {
  LtiSdkLaunchOptions,
  SearchFields,
  SharedTagsProps,
} from "@examspark/types/dist/components/activity-editor";

import { ExamEditorContext } from "../../exam-editor/examEditor";
import ActivityEditorObjectives from "../settings/ActivityEditorObjectives";

import ActivityEditorBasic from "./ActivityEditorBasic";
import { ActivityEditorItems } from "./ActivityEditorItems";
import ActivityEditorPreview from "./ActivityEditorPreview";

const Styled = styled.div`
  font-family:
    proxima-nova,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    "Roboto",
    "Oxygen",
    "Ubuntu",
    "Cantarell",
    "Fira Sans",
    "Droid Sans",
    "Helvetica Neue",
    sans-serif;
  height: 100%;
  min-height: 0;
`;

const StyledTabs = styled.div`
  padding: 1rem 0 0;

  & button {
    padding: 1.5rem 1rem;
  }
`;
const StyledTitlebar = styled.div`
  padding: 0.7rem 1rem;
  border-bottom: solid 1px #ddd;
  background-color: #f7f7f7;
  display: flex;
  align-items: center;
  gap: 1rem;

  input {
    width: 100%;
  }
`;

const StyledTitlebarLabel = styled.div`
  color: #666;
`;

type FilterOptions = {
  show?: boolean;
};

export type ItemPickerFiltersConfig = Partial<{
  bank: FilterOptions;
  blooms: FilterOptions;
  course: FilterOptions;
  customTags: Array<{
    key: string;
    label?: string;
  }>;
  deliveryMediaType: FilterOptions;
  dok: FilterOptions;
  grade: FilterOptions;
  interaction: FilterOptions;
  language: FilterOptions;
  objective: FilterOptions &
    Partial<{
      title: string;
      requirePrecondition: Array<keyof SearchFields>;
    }>;
  subject: FilterOptions;
  unit: FilterOptions &
    Partial<{
      title: string;
      courseSelectPlaceholder: string;
      noUnitResultsLabel: string;
      courseIds: string[];
    }>;
}>;

type ShowOption = Partial<{
  show: boolean;
}>;

export type ActivityEditorConfig = Partial<
  {
    allowItemCreation: boolean;
    allowItemCustomization: boolean;
    enablePassagePicker: boolean;
    readOnly?: boolean;
    guidedBuilder: Partial<{
      show: boolean;
    }>;
    visibility: ResourceVisibilityType;
    fields: Partial<{
      subject: Partial<{
        show: boolean;
        required: true;
      }>;
    }>;
    titlebar: Partial<{
      hide: boolean;
      disableTitleField: boolean;
    }>;
    preview: Partial<{
      enableFeedback: boolean;
    }>;
    objectives: Partial<{
      show: boolean;
      requireSelection: boolean;
      warnOnNoSelection: boolean;
    }>;
    activitySettings: Partial<{
      show: boolean;
      fields: Partial<{
        title: ShowOption;
        subject: ShowOption;
        grade: ShowOption;
        language: ShowOption;
        maturity: ShowOption;
      }>;
    }>;
    itemPicker: {
      filters?: ItemPickerFiltersConfig;
      initialFilter?: SearchFields;
      baseFilter?: SearchableResourceFilter;
    };
  } & SharedTagsProps
>;

export const ActivityEditorConfigContext =
  React.createContext<ActivityEditorConfig>({});

/**
 * This is the *better* hybrid builder/picker exam builder that will replace all
 * other forms of creating an activity/exam.
 */
const ActivityEditor = ({
  initialTab,
  tabOverride,
  config,
  showPreferences,
  isTitleFieldDisabled,
}: Partial<{
  showBasic?: boolean;
  showPreferences?: boolean;
  initialTab: string;
  tabOverride?: Partial<{
    basic: React.ReactNode;
    items: React.ReactNode;
    preview: React.ReactNode;
  }>;
  config?: ActivityEditorConfig &
    Pick<LtiSdkLaunchOptions, "contentFilter" | "createWithTags">;
  isTitleFieldDisabled?: boolean;
}>) => {
  const [tab, setTab] = useState<string>(initialTab ?? "basic");
  const [state, dispatch] = useContext(ExamEditorContext);

  return (
    <ActivityEditorConfigContext.Provider value={config ?? {}}>
      <Styled>
        <VFlex>
          {config?.titlebar?.hide !== true && (
            <StyledTitlebar>
              <StyledTitlebarLabel>Title:</StyledTitlebarLabel>
              <div style={{ flex: 1 }}>
                <TextInput
                  placeholder="Untitled Exam"
                  data-cy="activity-editor-title"
                  disabled={config?.readOnly}
                  value={state.name}
                  onChange={(e) => {
                    dispatch({
                      type: "UPDATE",
                      data: {
                        ...state,
                        name: e.target.value,
                      },
                    });
                  }}
                />
              </div>
            </StyledTitlebar>
          )}
          <StyledTabs data-cy="activity-editor-tabs">
            <Tabs value={tab} onChange={(value) => setTab(value)}>
              <Tabs.TabItem value="items">
                Items ({state.questions.length})
              </Tabs.TabItem>
              {config?.activitySettings?.show && (
                <Tabs.TabItem value="basic">Settings</Tabs.TabItem>
              )}
              {config?.objectives?.show && (
                <Tabs.TabItem value="objectives">Standards</Tabs.TabItem>
              )}
              <Tabs.TabItem value="preview">Preview</Tabs.TabItem>
            </Tabs>
          </StyledTabs>
          <VFlex.Main>
            {tab === "basic" &&
              (tabOverride?.basic ? (
                tabOverride.basic
              ) : (
                <ActivityEditorBasic showPreferences={showPreferences} />
              ))}
            {tab === "objectives" &&
              (tabOverride?.basic ? (
                tabOverride.basic
              ) : (
                <ActivityEditorObjectives />
              ))}
            {tab === "items" &&
              (tabOverride?.items ? (
                tabOverride.items
              ) : (
                <ActivityEditorItems />
              ))}
            {tab === "preview" &&
              (tabOverride?.preview ? (
                tabOverride.preview
              ) : (
                <ActivityEditorPreview />
              ))}
          </VFlex.Main>
        </VFlex>
      </Styled>
    </ActivityEditorConfigContext.Provider>
  );
};

export default ActivityEditor;
